import { useEffect, useState } from "react";
import { useIsMobile } from "../utils/useIsMobile";

// Shared "add to home screen" install logic — used by the post-login nudge
// (components/mobile/InstallPrompt.tsx) and the pre-login button on
// Login.tsx. Chrome/Android exposes `beforeinstallprompt`, which we capture
// and can trigger programmatically; iOS Safari has no install API (Add to
// Home Screen only exists behind the Share sheet), so that path is
// instructional only. See InstallPrompt.tsx for why the manifest alone
// (display:"standalone") isn't enough — nobody was ever told to install.
//
// Mobile-only by design: desktop Chrome also fires `beforeinstallprompt`
// (desktop PWA install is a real Chrome feature), but "Install this app" /
// "Add to your home screen" copy reads as mobile-specific and confusing on
// a desktop browser — this is a phone/tablet affordance, not a desktop one.
// Gated here (not per-consumer) so every current and future caller of this
// hook inherits the restriction automatically.

export function isStandalone(): boolean {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    );
  } catch {
    return false;
  }
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* non-fatal */ }
    setDeferredPrompt(null);
    return true;
  };

  return {
    canInstall: isMobile && !!deferredPrompt,
    promptInstall,
    isIOS: isMobile && isIOS(),
    isStandalone: isStandalone(),
  };
}
